<div class="zag-rrule-editor__header">
  @if (showSummary()) {
    <div class="zag-rrule-editor__summary">
      <span class="zag-rrule-editor__summary-icon" zagIcon="event-repeat"></span>
      <span class="zag-rrule-editor__summary-text">{{ summary() || ('calendars.rrule.summary.empty' | translate) }}</span>
    </div>
  }

  @if (allowTextMode()) {
    <div class="zag-rrule-editor__mode" role="tablist" [attr.aria-label]="'calendars.rrule.mode.switchLabel' | translate">
      <button
        type="button"
        role="tab"
        class="zag-rrule-editor__mode-btn"
        [class.zag-rrule-editor__mode-btn--active]="mode() === 'visual'"
        [attr.aria-selected]="mode() === 'visual'"
        [disabled]="disabled()"
        (click)="setMode('visual')"
      >
        <span zagIcon="tune"></span>
        {{ 'calendars.rrule.mode.visual' | translate }}
      </button>
      <button
        type="button"
        role="tab"
        class="zag-rrule-editor__mode-btn"
        [class.zag-rrule-editor__mode-btn--active]="mode() === 'text'"
        [attr.aria-selected]="mode() === 'text'"
        [disabled]="disabled()"
        (click)="setMode('text')"
      >
        <span zagIcon="code"></span>
        {{ 'calendars.rrule.mode.text' | translate }}
      </button>
    </div>
  }
</div>

<div class="zag-rrule-editor__body" [class.zag-rrule-editor__body--text]="mode() === 'text'">
  <!-- ── Visual mode ────────────────────────────────────────────────── -->
  @if (mode() === 'visual') {
    <div class="zag-rrule-editor__sections">
      <!-- Frequency + interval -->
      <section class="zag-rrule-editor__section">
        <header class="zag-rrule-editor__section-header">
          <h4 class="zag-rrule-editor__section-title">{{ 'calendars.rrule.repeats.title' | translate }}</h4>
        </header>

        <div class="zag-rrule-editor__freq-row">
          <zag-recurrence-interval-input
            [formControl]="intervalControl"
            [frequencies]="intervalFrequencies()"
            [max]="999"
            chipClass="zag-rrule-editor__chip--freq"
          />
          @if (allowSubDailyFrequencies()) {
            <button
              type="button"
              class="zag-rrule-editor__freq-toggle"
              [disabled]="disabled()"
              (click)="toggleSubDaily()"
              [attr.aria-label]="'calendars.rrule.repeats.subDailyToggle' | translate"
            >
              <span [zagIcon]="showSubDailyFrequencies() ? 'expand-less' : 'expand-more'"></span>
            </button>
          }
        </div>
      </section>

      <!-- Weekly: weekday picker -->
      @if (descriptor().frequency === 'weekly') {
        <section class="zag-rrule-editor__section">
          <header class="zag-rrule-editor__section-header">
            <h4 class="zag-rrule-editor__section-title">{{ 'calendars.rrule.byDay.weeklyTitle' | translate }}</h4>
          </header>

          <div class="zag-rrule-editor__weekday-chips" role="group" [attr.aria-label]="'calendars.rrule.byDay.weeklyTitle' | translate">
            @for (day of weekdays; track day) {
              <button
                type="button"
                class="zag-rrule-editor__chip zag-rrule-editor__chip--weekday"
                [class.zag-rrule-editor__chip--active]="isWeekdaySelected(day)"
                [attr.aria-pressed]="isWeekdaySelected(day)"
                [disabled]="disabled()"
                (click)="onWeekdayToggle(day)"
              >
                {{ weekdayLabelKeys[day] | translate }}
              </button>
            }
          </div>
          @if (descriptor().byDay?.length === 0) {
            <p class="zag-rrule-editor__section-hint">{{ 'calendars.rrule.byDay.weeklyEmpty' | translate }}</p>
          }
        </section>
      }

      <!-- Monthly / Yearly: day-of-month vs nth weekday -->
      @if (descriptor().frequency === 'monthly' || descriptor().frequency === 'yearly') {
        @if (descriptor().frequency === 'yearly') {
          <section class="zag-rrule-editor__section">
            <header class="zag-rrule-editor__section-header">
              <h4 class="zag-rrule-editor__section-title">{{ 'calendars.rrule.byMonth.yearlyTitle' | translate }}</h4>
              <p class="zag-rrule-editor__section-hint">{{ 'calendars.rrule.byMonth.yearlyHint' | translate }}</p>
            </header>
            <div class="zag-rrule-editor__month-chips" role="group" [attr.aria-label]="'calendars.rrule.byMonth.yearlyTitle' | translate">
              @for (m of months; track m) {
                <button
                  type="button"
                  class="zag-rrule-editor__chip zag-rrule-editor__chip--month"
                  [class.zag-rrule-editor__chip--active]="descriptor().byMonth?.includes(m)"
                  [disabled]="disabled()"
                  [attr.aria-pressed]="descriptor().byMonth?.includes(m)"
                  (click)="onMonthToggle(m)"
                >
                  {{ monthLabelKeys[$index] | translate }}
                </button>
              }
            </div>
          </section>
        }

        <section class="zag-rrule-editor__section">
          <header class="zag-rrule-editor__section-header">
            <h4 class="zag-rrule-editor__section-title">{{ 'calendars.rrule.repeatsOn.title' | translate }}</h4>
          </header>

          <div class="zag-rrule-editor__monthly-mode-tabs" role="tablist" [attr.aria-label]="'calendars.rrule.repeatsOn.title' | translate">
            <button
              type="button"
              role="tab"
              class="zag-rrule-editor__monthly-mode-tab"
              [class.zag-rrule-editor__monthly-mode-tab--active]="monthlyMode() === 'day'"
              [attr.aria-selected]="monthlyMode() === 'day'"
              [disabled]="disabled()"
              (click)="onMonthlyModeChange('day')"
            >
              {{ 'calendars.rrule.repeatsOn.dayOfMonth' | translate }}
            </button>
            <button
              type="button"
              role="tab"
              class="zag-rrule-editor__monthly-mode-tab"
              [class.zag-rrule-editor__monthly-mode-tab--active]="monthlyMode() === 'weekday'"
              [attr.aria-selected]="monthlyMode() === 'weekday'"
              [disabled]="disabled()"
              (click)="onMonthlyModeChange('weekday')"
            >
              {{ 'calendars.rrule.repeatsOn.nthWeekday' | translate }}
            </button>
          </div>

          @if (monthlyMode() === 'day') {
            <div class="zag-rrule-editor__day-chips" role="group" [attr.aria-label]="'calendars.rrule.repeatsOn.dayOfMonth' | translate">
              @for (day of daysOfMonth; track day) {
                <button
                  type="button"
                  class="zag-rrule-editor__chip zag-rrule-editor__chip--day"
                  [class.zag-rrule-editor__chip--active]="descriptor().byMonthDay?.includes(day)"
                  [disabled]="disabled()"
                  [attr.aria-pressed]="descriptor().byMonthDay?.includes(day)"
                  (click)="onDayOfMonthToggle(day)"
                >
                  {{ day }}
                </button>
              }
              <button
                type="button"
                class="zag-rrule-editor__chip zag-rrule-editor__chip--day zag-rrule-editor__chip--last"
                [class.zag-rrule-editor__chip--active]="hasLastDayOfMonth()"
                [disabled]="disabled()"
                [attr.aria-pressed]="hasLastDayOfMonth()"
                (click)="onLastDayOfMonthToggle()"
              >
                {{ 'calendars.rrule.repeatsOn.lastDay' | translate }}
              </button>
            </div>
          } @else {
            <div class="zag-rrule-editor__nth-row">
              <span class="zag-rrule-editor__inline-label">{{ 'calendars.rrule.repeatsOn.onThe' | translate }}</span>
              <zag-dropdown
                class="zag-rrule-editor__nth-position"
                [options]="nthPositionOptions()"
                [value]="positionalN()"
                (selectionChange)="onPositionalNChange($event)"
                [disabled]="disabled()"
              />
              <zag-dropdown
                class="zag-rrule-editor__nth-day"
                [options]="weekdayDropdownOptions()"
                [value]="positionalDay()"
                (selectionChange)="onPositionalDayChange($event)"
                [disabled]="disabled()"
              />
            </div>
          }
        </section>
      }

      <!-- Ends -->
      <section class="zag-rrule-editor__section">
        <header class="zag-rrule-editor__section-header">
          <h4 class="zag-rrule-editor__section-title">{{ 'calendars.rrule.ends.title' | translate }}</h4>
        </header>

        <div class="zag-rrule-editor__ends" role="radiogroup" [attr.aria-label]="'calendars.rrule.ends.title' | translate">
          @if (showNever()) {
            <label class="zag-rrule-editor__ends-option">
              <input
                type="radio"
                name="zag-rrule-editor-ends"
                [checked]="endMode() === 'never'"
                [disabled]="disabled()"
                (change)="onEndModeChange('never')"
              />
              <span>{{ 'calendars.rrule.ends.never' | translate }}</span>
            </label>
          }

          <label class="zag-rrule-editor__ends-option">
            <input
              type="radio"
              name="zag-rrule-editor-ends"
              [checked]="endMode() === 'count'"
              [disabled]="disabled()"
              (change)="onEndModeChange('count')"
            />
            <span>{{ 'calendars.rrule.ends.afterCount' | translate }}</span>
            <zag-number-input
              class="zag-rrule-editor__count-input"
              [value]="descriptor().count ?? null"
              (valueChange)="onCountChange($event)"
              [min]="1"
              [max]="maxCount()"
              [disabled]="disabled() || endMode() !== 'count'"
              [clearable]="false"
            />
            <span class="zag-rrule-editor__inline-suffix">{{ 'calendars.rrule.ends.occurrences' | translate }}</span>
          </label>

          @if (showUntil()) {
            <label class="zag-rrule-editor__ends-option">
              <input
                type="radio"
                name="zag-rrule-editor-ends"
                [checked]="endMode() === 'until'"
                [disabled]="disabled()"
                (change)="onEndModeChange('until')"
              />
              <span>{{ 'calendars.rrule.ends.onDate' | translate }}</span>
              <zag-date-picker
                class="zag-rrule-editor__until-input"
                [ngModel]="descriptor().until ?? ''"
                (ngModelChange)="onUntilChange($event)"
                [placeholder]="'calendars.rrule.ends.selectDate' | translate"
                [disabled]="disabled() || endMode() !== 'until'"
              />
            </label>
          }
        </div>
      </section>

      <!-- Advanced -->
      @if (allowAdvanced()) {
        <section class="zag-rrule-editor__section zag-rrule-editor__section--advanced">
          <button
            type="button"
            class="zag-rrule-editor__advanced-toggle"
            [disabled]="disabled()"
            [attr.aria-expanded]="showAdvanced()"
            (click)="toggleAdvanced()"
          >
            <span [zagIcon]="showAdvanced() ? 'expand-less' : 'expand-more'"></span>
            {{ 'calendars.rrule.advanced.title' | translate }}
          </button>

          @if (showAdvanced()) {
            <div class="zag-rrule-editor__advanced-grid">
              <!-- BYSETPOS -->
              <div class="zag-rrule-editor__advanced-field">
                <label class="zag-rrule-editor__advanced-label" for="zag-rrule-editor-bysetpos">
                  {{ 'calendars.rrule.advanced.bySetPos' | translate }}
                  <span class="zag-rrule-editor__advanced-hint">{{ 'calendars.rrule.advanced.bySetPosHint' | translate }}</span>
                </label>
                <zag-input
                  [id]="'zag-rrule-editor-bysetpos'"
                  [value]="advancedDraft().bySetPos"
                  (valueChange)="onAdvancedListChange('bySetPos', $event, { min: -366, max: 366, allowNegative: true })"
                  [disabled]="disabled()"
                  [placeholder]="'calendars.rrule.advanced.bySetPosPlaceholder' | translate"
                />
              </div>

              <!-- BYHOUR -->
              <div class="zag-rrule-editor__advanced-field">
                <label class="zag-rrule-editor__advanced-label" for="zag-rrule-editor-byhour">
                  {{ 'calendars.rrule.advanced.byHour' | translate }}
                  <span class="zag-rrule-editor__advanced-hint">{{ 'calendars.rrule.advanced.byHourHint' | translate }}</span>
                </label>
                <zag-input
                  [id]="'zag-rrule-editor-byhour'"
                  [value]="advancedDraft().byHour"
                  (valueChange)="onAdvancedListChange('byHour', $event, { min: 0, max: 23 })"
                  [disabled]="disabled()"
                  [placeholder]="'calendars.rrule.advanced.byHourPlaceholder' | translate"
                />
              </div>

              <!-- BYMINUTE -->
              <div class="zag-rrule-editor__advanced-field">
                <label class="zag-rrule-editor__advanced-label" for="zag-rrule-editor-byminute">
                  {{ 'calendars.rrule.advanced.byMinute' | translate }}
                  <span class="zag-rrule-editor__advanced-hint">{{ 'calendars.rrule.advanced.byMinuteHint' | translate }}</span>
                </label>
                <zag-input
                  [id]="'zag-rrule-editor-byminute'"
                  [value]="advancedDraft().byMinute"
                  (valueChange)="onAdvancedListChange('byMinute', $event, { min: 0, max: 59 })"
                  [disabled]="disabled()"
                  [placeholder]="'calendars.rrule.advanced.byMinutePlaceholder' | translate"
                />
              </div>

              <!-- BYSECOND -->
              <div class="zag-rrule-editor__advanced-field">
                <label class="zag-rrule-editor__advanced-label" for="zag-rrule-editor-bysecond">
                  {{ 'calendars.rrule.advanced.bySecond' | translate }}
                  <span class="zag-rrule-editor__advanced-hint">{{ 'calendars.rrule.advanced.bySecondHint' | translate }}</span>
                </label>
                <zag-input
                  [id]="'zag-rrule-editor-bysecond'"
                  [value]="advancedDraft().bySecond"
                  (valueChange)="onAdvancedListChange('bySecond', $event, { min: 0, max: 59 })"
                  [disabled]="disabled()"
                  [placeholder]="'calendars.rrule.advanced.bySecondPlaceholder' | translate"
                />
              </div>

              <!-- BYYEARDAY -->
              <div class="zag-rrule-editor__advanced-field">
                <label class="zag-rrule-editor__advanced-label" for="zag-rrule-editor-byyearday">
                  {{ 'calendars.rrule.advanced.byYearDay' | translate }}
                  <span class="zag-rrule-editor__advanced-hint">{{ 'calendars.rrule.advanced.byYearDayHint' | translate }}</span>
                </label>
                <zag-input
                  [id]="'zag-rrule-editor-byyearday'"
                  [value]="advancedDraft().byYearDay"
                  (valueChange)="onAdvancedListChange('byYearDay', $event, { min: -366, max: 366, allowNegative: true })"
                  [disabled]="disabled()"
                  [placeholder]="'calendars.rrule.advanced.byYearDayPlaceholder' | translate"
                />
              </div>

              <!-- BYWEEKNO -->
              <div class="zag-rrule-editor__advanced-field">
                <label class="zag-rrule-editor__advanced-label" for="zag-rrule-editor-byweekno">
                  {{ 'calendars.rrule.advanced.byWeekNo' | translate }}
                  <span class="zag-rrule-editor__advanced-hint">{{ 'calendars.rrule.advanced.byWeekNoHint' | translate }}</span>
                </label>
                <zag-input
                  [id]="'zag-rrule-editor-byweekno'"
                  [value]="advancedDraft().byWeekNo"
                  (valueChange)="onAdvancedListChange('byWeekNo', $event, { min: -53, max: 53, allowNegative: true })"
                  [disabled]="disabled()"
                  [placeholder]="'calendars.rrule.advanced.byWeekNoPlaceholder' | translate"
                />
              </div>

              <!-- WKST -->
              <div class="zag-rrule-editor__advanced-field">
                <label class="zag-rrule-editor__advanced-label" for="zag-rrule-editor-wkst">
                  {{ 'calendars.rrule.advanced.wkst' | translate }}
                  <span class="zag-rrule-editor__advanced-hint">{{ 'calendars.rrule.advanced.wkstHint' | translate }}</span>
                </label>
                <zag-dropdown
                  [id]="'zag-rrule-editor-wkst'"
                  [options]="wkstOptions()"
                  [value]="descriptor().wkst ?? 'MO'"
                  (selectionChange)="onWkstChange($event)"
                  [disabled]="disabled()"
                />
              </div>
            </div>
          }
        </section>
      }
    </div>
  }

  <!-- ── Text mode ──────────────────────────────────────────────────── -->
  @if (mode() === 'text') {
    <div class="zag-rrule-editor__text-pane">
      <label class="zag-rrule-editor__text-label" for="zag-rrule-editor-text">
        {{ 'calendars.rrule.text.label' | translate }}
      </label>
      <textarea
        id="zag-rrule-editor-text"
        class="zag-rrule-editor__textarea"
        rows="3"
        spellcheck="false"
        autocapitalize="characters"
        [value]="textDraft()"
        [disabled]="disabled()"
        (input)="onTextInput($any($event.target).value)"
      ></textarea>

      @if (textError(); as err) {
        <p class="zag-rrule-editor__text-error">
          <span zagIcon="error"></span>
          {{ err }}
        </p>
      } @else {
        <p class="zag-rrule-editor__text-hint">
          <span zagIcon="info"></span>
          {{ 'calendars.rrule.text.hint' | translate }}
        </p>
      }

      <div class="zag-rrule-editor__text-actions">
        <button type="button" zagButton="outlined" zagSize="sm" [disabled]="disabled() || !value()" (click)="copyRrule()">
          <span zagIcon="content-copy"></span>
          {{ 'calendars.rrule.text.copy' | translate }}
        </button>
      </div>
    </div>
  }
</div>

<!-- ── Preview ─────────────────────────────────────────────────────────── -->
@if (showPreview() && previewOccurrencesFormatted().length > 0) {
  <aside class="zag-rrule-editor__preview" aria-live="polite">
    <header class="zag-rrule-editor__preview-header">
      <span zagIcon="event"></span>
      <span>{{ 'calendars.rrule.preview.title' | translate }}</span>
    </header>
    <ol class="zag-rrule-editor__preview-list">
      @for (occ of previewOccurrencesFormatted(); track $index) {
        <li class="zag-rrule-editor__preview-item">{{ occ.label }}</li>
      }
    </ol>
  </aside>
}
